/**
 * Cookie / tracking consent state.
 *
 * Persisted in localStorage under two keys:
 *   - `cookie-consent-v1` : timestamped record of the user's choice so the
 *     banner is not shown twice. Bump the version to re-prompt everyone.
 *   - `analytics-consent` : `granted` / `denied`, consumed by the Sentry telemetry seam.
 *
 * Keep this file framework-agnostic — it runs in both web and Capacitor
 * WebView contexts and must not import React.
 */

export const CONSENT_VERSION = 'v1';
const STORAGE_KEY = `cookie-consent-${CONSENT_VERSION}`;
export const ANALYTICS_CONSENT_STORAGE_KEY = 'analytics-consent';

export type ConsentChoice = 'essential' | 'all' | 'denied';
export type AnalyticsConsentState = 'granted' | 'denied';

export interface ConsentRecord {
  choice: ConsentChoice;
  decidedAt: string; // ISO string
  version: typeof CONSENT_VERSION;
}

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    // Probe to catch Safari private mode / iframe quirks.
    const probe = '__vw_consent_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

export function getConsent(): ConsentRecord | null {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentRecord>;
    if (!parsed?.choice || parsed.version !== CONSENT_VERSION) return null;
    return {
      choice: parsed.choice,
      decidedAt: parsed.decidedAt ?? new Date().toISOString(),
      version: CONSENT_VERSION,
    };
  } catch {
    return null;
  }
}

export function setConsent(choice: ConsentChoice): ConsentRecord {
  const record: ConsentRecord = {
    choice,
    decidedAt: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  const storage = safeStorage();
  if (storage) {
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(record));
      storage.setItem(
        ANALYTICS_CONSENT_STORAGE_KEY,
        choice === 'all' ? 'granted' : 'denied',
      );
    } catch {
      /* ignore — worst case the banner reappears next session */
    }
  }
  return record;
}

export function clearConsent(): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
    storage.removeItem(ANALYTICS_CONSENT_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

export function getAnalyticsConsentState(): AnalyticsConsentState {
  const storage = safeStorage();
  if (!storage) return 'denied';
  try {
    return storage.getItem(ANALYTICS_CONSENT_STORAGE_KEY) === 'granted' ? 'granted' : 'denied';
  } catch {
    return 'denied';
  }
}

export function hasAnalyticsConsent(): boolean {
  return getAnalyticsConsentState() === 'granted';
}
